/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {calculateHeight, errorMsg, isSmWidth} from "../../../../Apis/functions";
import {
    DispatchMaterialRequest,
    GetMaterialRequestById
} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import routes from "../../../../Routes/routes";
import {FILE_MODEL} from "../../Visitor/Visitor/Model/Model";
import {MATERIAL_REQUEST_DISPATCH_MODEL, showDispatchBadge} from "../Model/Model";
import {toast} from "react-toastify";
import {USER_TYPE_NAME_MODEL} from "../../../../enums/userTypeEnum";

const MaterialDispatchApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const { id } = useParams();
    const location = useLocation();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    // A guard carries no permissions, its user type opens the folios it works on at the gate
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const [modified, setModified] = useState(false);
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [item, setItem] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [evidence, setEvidence] = useState([]);
    const showBadge = showDispatchBadge(item?.dispatchStatusId);
    const [dispatchStatusId, setDispatchStatusId] = useState(null);
    const [emptyFields, setEmptyFields] = useState(false);

    /**
     * Effect: Adjusts dynamic container heights based on screen size
     * and dispatch badge visibility.
     *
     * Behavior:
     * - Skips execution on small screen devices.
     * - Dynamically calculates available height for:
     *   - Main card container
     *   - Table container
     *   - Card content area
     *   - Table content area
     * - Uses a different bottom spacing depending on whether
     *   the dispatch badge is visible.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = showBadge ? 80 : 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, [showBadge]);

    useEffect(() => {
        getInfo();
    }, []);

    /**
     * Effect: Re-fetches batch information when `modified` flag changes.
     *
     * - If `modified` is true:
     *   - Calls `getInfo` to refresh data
     *   - Resets `modified` flag
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Retrieves material request information by identifier.
     *
     * Behavior:
     * - Fetches material request details from backend service.
     * - Updates:
     *   - Request detail information.
     *   - Associated evidence files.
     * - Maps evidence files into FILE_MODEL instances.
     * - Displays backend errors when request fails.
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetMaterialRequestById(id)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setItem(data);
                    const aux = data?.evidence?.map(e => FILE_MODEL.set(e));
                    if(aux) setEvidence(aux);
                }else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the view the dispatch was opened from, the material
     * request main view when there is no origin.
     *
     * @returns {void}
     */
    const goBack = () => {
        navigate(location.state?.from || routes.materialRequest.main)
    }

    /**
     * Opens the dispatch confirmation modal.
     *
     * Behavior:
     * - Validates required item evidence information.
     * - Sets dispatch status to DISPATCHED.
     * - Displays confirmation modal if validation succeeds.
     *
     * @returns {void}
     */
    const openDispatchModal = () => {
        if(validFields()){
            setDispatchStatusId(STATUS_MODEL.DISPATCHED);
            setShowConfirmModal(true);
        }
    }

    /**
     * Opens the discrepancy report confirmation modal.
     *
     * Behavior:
     * - Validates required item evidence information.
     * - Sets dispatch status to DISCREPANCY.
     * - Displays confirmation modal if validation succeeds.
     *
     * @returns {void}
     */
    const openReportModal = () => {
        if(validFields()) {
            setDispatchStatusId(STATUS_MODEL.DISCREPANCY);
            setShowConfirmModal(true);
        }
    }

    /**
     * Validates that all material request items
     * contain evidence information.
     *
     * Behavior:
     * - Checks whether any item is missing evidence.
     * - Displays warning notification if validation fails.
     * - Updates empty field validation state.
     *
     * @returns {boolean} Returns true if all fields are valid,
     * otherwise false.
     */
    const validFields = () => {
        const invalid = item?.items?.some(e => e.itemEvidence == null);
        if(invalid){
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
        }else{
            setEmptyFields(false);
        }
        return !invalid
    }

    /**
     * Handles dispatch or discrepancy report actions.
     *
     * Behavior:
     * - Sends dispatch information to backend service.
     * - Updates request dispatch status.
     * - Displays success notification based on action type.
     * - Refreshes request information after success.
     * - Closes confirmation modal.
     * - Displays backend errors when request fails.
     *
     * @param {string} dispatchReason - Comment or reason associated
     * with the dispatch or discrepancy action.
     *
     * @returns {void}
     */
    const handleActionModal = (dispatchReason) => {
        dispatch(DispatchMaterialRequest(MATERIAL_REQUEST_DISPATCH_MODEL.toRegister({
            id,
            dispatchStatusId,
            dispatchReason
        }))).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                setModified(true);
                setShowConfirmModal(false);
                toast.success(t( dispatchStatusId === STATUS_MODEL.DISPATCHED ? "has_been_dispatched_successfully" : "has_been_reported_successfully"));
            } else {
                errorMsg(code);
            }
        });
    }

    return {
        t,
        navigate,
        DEV,
        isGuard,
        permission,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        item,
        evidence,
        showBadge,
        showConfirmModal,
        setShowConfirmModal,
        dispatchStatusId,
        setModified,
        emptyFields,
        openDispatchModal,
        openReportModal,
        handleActionModal,
        goBack
    };
};

export default MaterialDispatchApp; // Exporting the component for use in other parts of the app
